/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import classnames from 'classnames';
import Close from '@mui/icons-material/Close';

import sortBy from 'lodash/sortBy';

import { registerField } from 'core/forms';

import { Button } from 'uikit/buttons';
import { Typo } from 'uikit/content';
import { Select } from 'uikit/selects';

import styles from '../styles/formFieldAutoCompleteSearch.module.scss';

const SelectedOptionItem = ({
  handleRemoveSelected = () => {},
  isReadOnly = false,
  selectedItem = {},
}) => (
  <li className={classnames(styles.item, isReadOnly && styles.isReadOnly)}>
    <div>
      <Typo variant="s02" weight="medium">{selectedItem.name}</Typo>
      <Typo className={styles.isSecondary} variant="s01">{selectedItem.code}</Typo>
    </div>
    {!isReadOnly && (
      <Button
        icon={<Close />}
        onClick={() => handleRemoveSelected(selectedItem.id)}
        size="xsmall"
        variant="ghost"
      />
    )}
  </li>
);

SelectedOptionItem.propTypes = {
  handleRemoveSelected: PropTypes.func,
  isReadOnly: PropTypes.bool,
  selectedItem: PropTypes.shape({
    code: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }),
};

const FormFieldAutoCompleteSearch = ({
  fieldId,
  schema,
  updateField,
  value: selectedOptions = [],
}) => {
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);

  const {
    handleAddOption, handleLoadOptions, sortSelectedOptionsBy, selectProps, CustomSelectedOptionItem, className, emptyStateText, isReadOnly, isDisabled, isSingleSelect, selectedItemsListClassName
  } = schema;

  const handleOnLoadOptions = useCallback(async (searchQuery) => {
    const selectedOptionIds = isSingleSelect ? [selectedOptions?.id] : selectedOptions?.map(({ id }) => id);
    setIsLoadingOptions(true);
    const loadedOptions = await handleLoadOptions(searchQuery) || [];
    setIsLoadingOptions(false);
    const newOptions = isSingleSelect ? loadedOptions : loadedOptions.filter(
      (option) => !selectedOptionIds?.includes(option.id),
    );
    setOptions(newOptions);
    return newOptions;
  }, [handleLoadOptions, selectedOptions]);

  useEffect(() => {
    if (!searchValue) {
      handleOnLoadOptions('');
    }
  }, [handleOnLoadOptions, searchValue]);

  const handleAddSelected = useCallback(({ value }) => {
    let updatedOptions;

    if (handleAddOption) {
      updatedOptions = handleAddOption({ selectedOptions, value });
    } else {
      updatedOptions = isSingleSelect
        ? value
        : sortBy([...(selectedOptions || []), { ...value }], sortSelectedOptionsBy);
    }

    setSearchValue('');
    updateField(updatedOptions);
  }, [selectedOptions, sortSelectedOptionsBy, updateField]);

  const handleFilterOption = useCallback(({ data }) => {
    if (isSingleSelect) return true;
    
    return !selectedOptions?.some(({ id: selectedId }) => selectedId === data.id);
  }, [selectedOptions]);

  const handleRemoveSelected = useCallback((optionId) => {
    const updatedOptions = selectedOptions.filter(
      ({ id: selectedId }) => selectedId !== optionId,
    );
    updateField(updatedOptions);
  }, [selectedOptions, updateField]);

  const handleOnInputChange = useCallback((searchQuery) => {
    setSearchValue(searchQuery);
  }, []);

  const hasSelectedOptions = Boolean(selectedOptions?.length);
  const SelectedOptionItemComponent = CustomSelectedOptionItem || SelectedOptionItem;

  return (
    <div className={classnames(styles.element, className)} data-testid={`form-field-autocomplete-search-${fieldId}`}>
      <Select
        isAsync
        isFluid
        isLoading={isLoadingOptions && !options.length}
        inputId={fieldId}
        defaultOptions={options}
        filterOption={handleFilterOption}
        inputValue={searchValue}
        loadOptions={handleOnLoadOptions}
        onChange={handleAddSelected}
        onFocus={() => handleOnLoadOptions('')}
        onInputChange={handleOnInputChange}
        value={isSingleSelect ? options?.find((option) => option.id === selectedOptions?.id) : null}
        isSearchable
        loadingMessage={() => i18n.t('loading')}
        noOptionsMessage={() => i18n.t('no-matches')}
        isDisabled={isDisabled}
        {...selectProps}
      />
      {hasSelectedOptions && !isSingleSelect && (
        <ul className={classnames(styles.selectedItemsList, selectedItemsListClassName, { [styles.isDefaultSelectedOptionItem]: !CustomSelectedOptionItem })}>
          {selectedOptions?.map((selectedItem) => (
            <SelectedOptionItemComponent
              key={selectedItem.id}
              selectedItem={selectedItem}
              isReadOnly={isReadOnly || isDisabled}
              handleRemoveSelected={handleRemoveSelected}
            />
          ))}
        </ul>
      )}
      {!hasSelectedOptions && emptyStateText && (
        <Typo variant="s02" weight="medium">
          {emptyStateText}
        </Typo>
      )}
    </div>
  );
};

FormFieldAutoCompleteSearch.propTypes = {
  fieldId: PropTypes.string.isRequired,
  schema: PropTypes.shape({
    className: PropTypes.string,
    CustomSelectedOptionItem: PropTypes.elementType,
    handleAddOption: PropTypes.func,
    handleLoadOptions: PropTypes.func.isRequired,
    emptyStateText: PropTypes.string,
    selectProps: PropTypes.shape({}),
    selectedItemsListClassName: PropTypes.string,
    sortSelectedOptionsBy: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    isReadOnly: PropTypes.bool,
    isDisabled: PropTypes.bool,
  }).isRequired,
  updateField: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
};

registerField('AutoCompleteSearch', React.memo(FormFieldAutoCompleteSearch));

export default FormFieldAutoCompleteSearch;
